import {
  Accordion,
  ButtonGroup,
  Flex, RadioGroup,
  Slider,
  SliderFilledTrack,
  SliderThumb,
  SliderTrack,
  Tag,
  Text
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  coverageTypes,
  planPaymentMode,
  bedroomEnum,
  backgroundTypeEnum,
} from 'common/utils/enums';
import {
  saveCoverageSelection,
  selectCoverageSelection,
  selectUpfrontSplitAmount,
  saveUpfrontSplitAmount,
} from 'redux/features/plans/plansSlice';
import { PrePurchaseCoverageCard } from 'components/coverages/coverage-card';
import ApplyButtons from 'components/apply-shared/apply-buttons';
import ApplyTitle from 'components/apply-shared/apply-title';
import ApplyContent from 'components/apply-shared/apply-content';
import { InfoDetail } from 'components/shared/member-info-card';
import FormatAsCurrency from 'common/utils/formatAsCurrency';
import ContactCard from 'components/shared/contact-card';
import useUserTracker from 'common/utils/user-tracker/useUserTracker';
import IconImage from 'common/components/icon-image';
import {
  calculateSavings,
  SavingsCalculator,
} from 'components/coverages/savings-calculator';
import { handleSmoothScroll } from 'common/hooks/handleSmoothScroll';
import Button from 'common/components/button';
import PaymentPlanCard from 'components/plans/payment-plan-card';
import SaveQuoteButton from 'components/plan-quote/save-quote-modal';
import EmptyPage from 'common/components/empty-page';
import { ApplyFormLayout } from 'common/layouts/apply-form';
import { useDefaultCoverageSelection } from 'hooks/useDefaultCoverageSelection';
import WhatsNextQuote from 'components/plan-quote/quote-whats-next';

const ShowQuote = ({
  onBackClick,
  onNextClick,
  planData,
  onSaveClick,
  isLoading,
}) => {
  // HOOKS
  const { trackPageLoad, trackCustomEvent } = useUserTracker();
  const dispatch = useDispatch();

  // SIDE EFFECTS
  // For some reason I can't use the tracker element, causes weird page loading issues
  useEffect(() => {
    trackPageLoad('Show Quote');
  }, []);

  // STATES
  const coverageSelection = useSelector(selectCoverageSelection);
  const upfrontSplit = useSelector(selectUpfrontSplitAmount);
  const [savings, setSavings] = useState({});

  // Set default coverage selection
  useDefaultCoverageSelection(true, false);
  const handleCoverageSelection = (value) => {
    dispatch(
      saveCoverageSelection({
        ...coverageSelection,
        [coverageTypes.LEAVE_ANYTIME]: value,
      })
    );
  };

  // DATA INITIALIZATION
  // Set up payment amounts
  const formatAmount = (amount) => {
    return FormatAsCurrency(amount, planData?.lease?.currency);
  };

  // Get the right fixed fee for the quote
  let fixedFee = 5;
  let rates = {};
  // Check if property exists
  if (planData?.property) {
    rates = planData?.property?.rates?.underwriting;
    fixedFee = rates?.monthlyPerUnitFee;
  }

  const leaseTerm = planData?.lease?.term; // Assume a 12-month term for quotes
  const leaveAnytime = planData?.coverage?.[coverageTypes.LEAVE_ANYTIME];
  const leaveAnytimeSubscriptionAmount =
    leaveAnytime?.payment?.[planPaymentMode.SUBSCRIPTION]?.amount;
  const leaveAnytimeTotalAmount = leaveAnytimeSubscriptionAmount * leaseTerm;
  const leaveAnytimeOnetimeAmount = leaveAnytimeTotalAmount; // No discount for quote

  // Initialize the slider value to 50% of the total if it hasn't been set.
  useEffect(() => {
    if (upfrontSplit === null && leaveAnytimeTotalAmount > 0) {
      dispatch(saveUpfrontSplitAmount(leaveAnytimeTotalAmount / 2));
    }
  }, [leaveAnytimeTotalAmount, upfrontSplit, dispatch]);

  const monthlySplitTotal = leaveAnytimeTotalAmount - upfrontSplit;
  const monthlySplitAmount = monthlySplitTotal / leaseTerm;

  // Calculate savings based on selected payment option
  useEffect(() => {
    let monthlyCost = null;
    let upfrontCost = null;

    const leaveAnytimeSelection =
      coverageSelection[coverageTypes.LEAVE_ANYTIME];

    if (leaveAnytimeSelection === planPaymentMode.ONE_TIME) {
      upfrontCost = leaveAnytimeOnetimeAmount;
    } else if (leaveAnytimeSelection === planPaymentMode.SPLIT) {
      monthlyCost = monthlySplitAmount;
      upfrontCost = upfrontSplit;
    } else if (leaveAnytimeSelection === planPaymentMode.SUBSCRIPTION) {
      monthlyCost = leaveAnytimeSubscriptionAmount;
    }

    setSavings(
      calculateSavings(
        planData?.property,
        leaveAnytime?.deductible || 0,
        planData?.lease,
        monthlyCost,
        upfrontCost
      )
    );
  }, [
    coverageSelection,
    planData,
    leaveAnytimeOnetimeAmount,
    leaveAnytimeSubscriptionAmount,
    monthlySplitAmount,
    upfrontSplit,
  ]);

  // Set up text for bedroom count
  let bedroomText = `${planData?.lease?.numBeds} bedroom`;
  if (planData?.lease?.numBeds === bedroomEnum.STUDIO) {
    bedroomText = 'a studio';
  }

  const brand = planData?.property?.brand;
  const isDarkBg = brand?.backgroundType === backgroundTypeEnum.BACKGROUND_DARK;

  return (
    <div className='flex flex-col w-full h-full'>
      <ApplyTitle
        title='Your ReLease Quote'
        subtitle={`${formatAmount(planData?.lease?.monthlyRent)} per month ${bedroomText} unit at ${planData?.property ? planData.property?.name : planData?.lease?.address?.streetAddress1}`}
        buttons={
          <ApplyButtons
            onNextClick={() => {
              onNextClick();
            }}
            onBackClick={() => {
              onBackClick();
            }}
            backTitle='Change Details'
            nextTitle='Purchase Now'
            isValid={true}
            moveOn={true}
          >
            <div className='hidden md:block'>
              <SaveQuoteButton planData={planData} />
            </div>
          </ApplyButtons>
        }
      />

      {/* main content */}
      <ApplyContent>
        {/* container */}
        <Flex className='w-full flex-col gap-8'>
          {/* plan coverages section */}
          {isLoading ? (
            <EmptyPage
              isLoading
              title='Preparing your quote'
              subtitle='Hold on—your ReLease quote will be ready in a few seconds.'
            />
          ) : (
            <>
              {planData?.property && (
                <ApplyFormLayout
                  className={`p-6 rounded-lg border relative overflow-clip ${
                    brand ? 'border-gray-200' : 'border-gray-200 bg-gray-50'
                  }`}
                  style={
                    brand?.bgColor ? { backgroundColor: brand.bgColor } : {}
                  }
                >
                  <Flex className='z-10 relative flex-col sm:flex-row gap-6 sm:gap-8 col-span-2'>
                    {brand && (
                      <img
                        src={
                          isDarkBg && brand?.logoAlt ? brand?.logo : brand?.logo
                        }
                        alt={planData?.property?.name}
                        className='max-w-[140px] max-h-[50px] object-contain'
                      />
                    )}
                    <Flex className='flex-col gap-1'>
                      <Text
                        className={`font-medium text-lg ${
                          isDarkBg ? 'text-white' : 'text-black'
                        }`}
                      >
                        ReLease Coverage at {planData?.property?.name}
                      </Text>
                      <Text
                        className={`text-base ${isDarkBg ? 'text-white opacity-60' : 'text-black opacity-60'}`}
                      >
                        {planData?.property?.address?.streetAddress1},{' '}
                        {planData?.property?.address?.city},{' '}
                        {planData?.property?.address?.state}{' '}
                        {planData?.property?.address?.postalCode}
                      </Text>
                    </Flex>
                  </Flex>
                  {brand?.backgroundHeroImage && (
                    <img
                      src={brand?.backgroundHeroImage}
                      alt={planData?.property?.name}
                      className='absolute inset-0 opacity-15 z-0 object-cover'
                    />
                  )}
                </ApplyFormLayout>
              )}
              <Flex className='flex-col gap-2'>
                <PrePurchaseCoverageCard
                  coverageType={coverageTypes.LEAVE_ANYTIME}
                  savings={
                    <Tag
                      colorScheme='green'
                      size='sm'
                      className='-mt-2 px-3 py-1.5 flex gap-2 w-fit cursor-pointer'
                      onClick={() => handleSmoothScroll('savings-calculator')}
                    >
                      <IconImage size='3xs' dark className='-mt-0.5' />
                      <span>
                        You're saving {savings?.savings} with ReLease
                        <span className='hidden md:inline'>
                          {' '}
                          if you move early
                        </span>
                      </span>
                    </Tag>
                  }
                  amount={
                    <ButtonGroup className='self-start sm:self-center'>
                      {/* <Button
                        title='Save Quote'
                        wFull={false}
                        theme='secondary'
                        onClick={onSaveClick}
                      /> */}
                      <SaveQuoteButton planData={planData} />
                      <Button
                        theme='secondary'
                        size='sm'
                        title="What's Next?"
                        onClick={() => {
                          handleSmoothScroll('whats-next');
                        }}
                      />
                    </ButtonGroup>
                  }
                  options={
                    <RadioGroup
                      className='flex flex-col divide-y divide-gray-200'
                      value={coverageSelection[coverageTypes.LEAVE_ANYTIME]}
                      onChange={handleCoverageSelection}
                    >
                      <PaymentPlanCard
                        title='Pay upfront'
                        subtitle={`One payment of ${formatAmount(leaveAnytimeOnetimeAmount)}`}
                        amount={formatAmount(leaveAnytimeOnetimeAmount)}
                        value={planPaymentMode.ONE_TIME}
                        recurring={false}
                      />
                      <PaymentPlanCard
                        title={`Pay in ${leaseTerm} monthly installments`}
                        subtitle={`Pay ${formatAmount(leaveAnytimeTotalAmount)} in ${leaseTerm} monthly installments of ${formatAmount(
                          leaveAnytimeSubscriptionAmount
                        )}`}
                        amount={formatAmount(leaveAnytimeSubscriptionAmount)}
                        value={planPaymentMode.SUBSCRIPTION}
                      />
                      <PaymentPlanCard
                        title='Customize my payment'
                        subtitle='Split your upfront and monthly payments to what works best for you'
                        value={planPaymentMode.SPLIT}
                        button={
                          coverageSelection[coverageTypes.LEAVE_ANYTIME] !==
                            planPaymentMode.SPLIT && (
                            <Button
                              theme='secondary'
                              size='sm'
                              title='Customize my payment'
                              onClick={() => {
                                dispatch(
                                  saveCoverageSelection({
                                    ...coverageSelection,
                                    [coverageTypes.LEAVE_ANYTIME]:
                                      planPaymentMode.SPLIT,
                                  })
                                );
                              }}
                            />
                          )
                        }
                      >
                        {coverageSelection[coverageTypes.LEAVE_ANYTIME] ===
                          planPaymentMode.SPLIT && (
                          <Flex className='pl-16 pr-6 sm:pt-3 pb-5 gap-3 sm:gap-2 w-full flex-col'>
                            <Flex className='w-full justify-between gap-3 items-center'>
                              {/* Upfront Payment Display */}
                              <Text className='font-medium text-xl'>
                                {formatAmount(upfrontSplit)}
                                <span className='block sm:inline text-md text-gray-500 font-normal'>
                                  {' '}
                                  upfront
                                </span>
                              </Text>

                              {/* Monthly Payment Display */}
                              <Text className='font-medium text-xl'>
                                {formatAmount(monthlySplitAmount)}
                                <span className='block sm:inline text-md text-gray-500 font-normal'>
                                  {' '}
                                  per month
                                </span>
                              </Text>
                            </Flex>

                            {/* Slider */}
                            <Slider
                              aria-label='upfront-payment-slider'
                              value={upfrontSplit || 0}
                              min={0}
                              max={leaveAnytimeTotalAmount}
                              step={1}
                              onChange={(val) =>
                                dispatch(saveUpfrontSplitAmount(val))
                              }
                            >
                              <SliderTrack>
                                <SliderFilledTrack className='bg-black' />
                              </SliderTrack>
                              <SliderThumb />
                            </Slider>
                            <Text className='text-sm text-gray-500 text-right'>
                              Your quote is based on a 12-month lease term.
                            </Text>
                          </Flex>
                        )}
                      </PaymentPlanCard>
                    </RadioGroup>
                  }
                >
                  <Accordion>
                    <InfoDetail
                      title='Coverage Period'
                      subtitle={
                        <span>
                          {leaseTerm} months{' '}
                          <span className='text-gray-500'>
                            (starting from your lease start date)
                          </span>
                        </span>
                      }
                    >
                      <Text>
                        Your coverage period starts when you move into your new
                        apartment on your lease start date and ends on your
                        lease end date. You are eligible to leave your lease during this period and receive benefits.
                      </Text>
                    </InfoDetail>
                    <InfoDetail
                      title='Deductible'
                      subtitle={
                        <span>
                          {FormatAsCurrency(
                            leaveAnytime?.deductible || 0,
                            'USD'
                          )}
                          <span className='text-gray-500'>
                            {' '}
                            (due at time of claim)
                          </span>
                        </span>
                      }
                    >
                      <Text>
                        Your coverage includes a deductible in the amount of{' '}
                        {FormatAsCurrency(leaveAnytime?.deductible || 0, 'USD')}
                        . This amount must be paid at the time of a claim before
                        ReLease begins covering the rest of your early
                        termination fees.
                      </Text>
                    </InfoDetail>
                    <InfoDetail
                      title='Notice Period'
                      subtitle={
                        <span>
                          {leaveAnytime?.noticePeriod || 3} days{' '}
                          <span className='text-gray-500'>
                            (before move-out date)
                          </span>
                        </span>
                      }
                    >
                      <Text>
                        You must provide at least {leaveAnytime?.noticePeriod || 3} days notice before your move-out date. 
                        This means you need to submit your claim at least {leaveAnytime?.noticePeriod || 3} days before 
                        the earliest date you want to move out.
                      </Text>
                    </InfoDetail>
                  </Accordion>
                </PrePurchaseCoverageCard>
                <Text className='px-2 text-sm text-gray-500'>
                  Your final price may change based on your actual lease term and
                  other details in your final lease agreement.
                </Text>
              </Flex>

              <SavingsCalculator
                property={planData?.property}
                deductible={leaveAnytime?.deductible || 0}
                noticePeriod={leaveAnytime?.noticePeriod || 3}
                lease={planData?.lease}
                savings={savings}
                onSaveClick={onSaveClick}
              />

              <Flex id='whats-next' className='pt-6 -mt-6'>
                <Flex className='flex-col gap-6 w-full p-6 border border-gray-200 bg-gray-50 rounded-md'>
                  <Flex className='flex-col sm:flex-row sm:justify-between gap-3 sm:items-center'>
                    <h2 className='text-lg font-medium'>What's Next?</h2>
                  </Flex>
                  <WhatsNextQuote planData={planData} />
                </Flex>
              </Flex>
            </>
          )}

          <ContactCard
            title='Need help understanding your quote?'
            subtitle='Contact us via email or phone to get help with your quote'
          />
        </Flex>
      </ApplyContent>
    </div>
  );
};

export default ShowQuote;
